<template>
  <main class="flex-1 flex relative z-0 overflow-hidden focus:outline-none h-screen-mobile-safe md:h-screen" tabindex="0">
    <div
      class="border-r border-gray-200 flex-shrink-0 flex-grow md:flex-grow-0 w-64 flex-col pt-2"
      :class="homeNumberId ? 'hidden md:flex' : 'flex'"
    >

      <div class="bg-white p-2 border-b flex justify-between flex-shrink-0">
        <Toggle  v-slot="{ toggle, didToggle }">
          <span class="relative z-0 inline-flex w-48 text-xs h-8">
            <div class="absolute inset-0 bg-gray-200 rounded-md inline-flex border-2 border-transparent">
              <div
                class="transform transition ease-in-out duration-200 w-1/2"
                :class="[ toggle == 0 ? 'translate-x-0' : 'translate-x-full' ]"
              >
                <div class="bg-white absolute inset-0 shadow rounded"></div>
              </div>
            </div>
            <button type="button" class="relative px-2 py-1 leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-none active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150 w-1/2 text-center" @click="didTriggerToggle(didToggle, false)">
              All
            </button>
            <button type="button" class="relative px-2 py-1 leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-none active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150 w-1/2 text-center" @click="didTriggerToggle(didToggle, true)">
              Unread
            </button>
          </span>
        </Toggle>

        <div class="w-8">
          <div class="relative z-0 inline-flex text-xs">
                <router-link
                  :to="{name: $route.name + 'MessageNew', params: { canGoBack: true}}"
                  tag="button"
                  type="button"
                  class="flex items-center justify-center border border-transparent rounded-md text-white bg-cardstock-green-600 hover:bg-cardstock-green-500 focus:outline-none focus:border-cardstock-green-700 focus:ring-cardstock-green active:bg-cardstock-green-700 transition ease-in-out duration-150 h-8 w-8"
                >
                <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <font-awesome-icon :icon="['far', 'plus']"></font-awesome-icon>
                </svg>
              </router-link>
          </div>
        </div>
      </div>

      <ConversationList
        v-if="!noConversations"
        :conversations="conversations"
        :didLoadMore="fetchMore"
      />
      <div class="overflow-y-auto flex-grow scrollbar" v-else>

        <div class="p-4 flex flex-col items-center justify-center my-24" v-if="noConversations && onlyUnread">
          <div class="text-5xl mb-4">👍</div>
          <div class="font-bold text-base text-center">Nothing left unread!</div>
          <div class="mt-1 text-sm text-center">Doesn't that just feel satisfying?</div>
        </div>
        <div class="p-4 flex flex-col items-center justify-center my-24" v-if="noConversations && !onlyUnread">
          <div class="text-5xl mb-4">👨‍💻👩‍💻</div>
          <div class="font-bold text-base text-center">You haven't started any conversations yet.</div>
          <router-link :to="{name: $route.name + 'MessageNew', params: { canGoBack: true}}" class="mt-1 text-sm text-center text-cool-gray-500 underline underline-cardstock-blue">Click here to send a message</router-link>
        </div>

      </div>

    </div>

    <router-view :currentUser="currentUser" :key="$route.params.homeNumberId || 'none'" :class="homeNumberId ? 'flex' : 'hidden md:flex'"/>
    <router-view name="modal"></router-view>
  </main>
</template>

<script>
import _filter from 'lodash-es/filter'
import _get from 'lodash-es/get'
import _size from 'lodash-es/size'
import _nth from 'lodash-es/nth'
import _orderBy from 'lodash-es/orderBy'
import _reverse from 'lodash-es/reverse'
import _uniqBy from 'lodash-es/uniqBy'
import _differenceBy from 'lodash-es/differenceBy'
import parse from 'date-fns/parse'

import { preload }  from '@/preloader'
import { tz }       from '@/tz'

import Page from '@/pages/Page.vue'

import ConversationList from '@/components/ConversationList.vue'
import Dropdown from '@/components/Dropdown.vue'
import Toggle from '@/components/Toggle.js'
import Modal from '@/components/Modal.vue'
import PageHeader from '@/components/PageHeader.vue'
import Panel from '@/components/Panel.vue'
import PanelDropdown from '@/components/PanelDropdown.vue'
import MessageRecipientForm from '@/components/MessageRecipientForm.vue'

import ConversationListQuery from '@/queries/ConversationList.gql'
import NewConversationSubscription from '@/subscriptions/NewConversation.gql'

export default {
  name: 'Conversations',
  components: {
    ConversationList,
    Dropdown,
    MessageRecipientForm,
    Modal,
    Page,
    PageHeader,
    Panel,
    PanelDropdown,
    Toggle
  },

  props: {
    homeNumberId: {
      required: false
    },
    pages: {
      type: Number,
      required: false
    },
    teamId: {
      required: true
    },
    currentUser: {
      required: true
    },
    homeNumberId: {
      default: undefined
    }
  },

  data() {
    return {
      onlyUnread: false,
      isToggling: false,
      cachedUnreadConversations: []
    }
  },

  beforeRouteEnter: (to, from, next) => {
    preload({
      callback: next,
      query: ConversationListQuery,
      queryName: "conversationList",
      variables: {
          tz: tz(),
          onlyUnread: false,
          teamId: to.params.teamId,
          messaged: true
      }
    })
  },

  beforeRouteUpdate: (to, from, next) => {
    preload({
      callback: next,
      query: ConversationListQuery,
      queryName: "conversationList",
      mountedVm: this,
      variables: {
          tz: tz(),
          onlyUnread: false,
          teamId: to.params.teamId,
          messaged: true
      }
    })
  },

  methods: {
    formatDatestamp (timestamp) {
      return parse(timestamp).toLocaleString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})
    },

    formatTimestamp (timestamp) {
      return parse(timestamp).toLocaleString('en-US', {hour: 'numeric', minute: 'numeric'})
    },

    size(item) {
      return _size(item || [])
    },

    fetchMore() {
      if (!this.isToggling) {
        if(this.onlyUnread) {
          this.fetchMoreUnread()
        } else {
          this.fetchMoreAll()
        }
      }
    },

    didTriggerToggle(didToggle, newOnlyUnread) {
      this.isToggling = true
      didToggle()
      this.onlyUnread = newOnlyUnread
      this.isToggling = false
      if (newOnlyUnread) {
        console.log('Filtering')
        this.cachedUnreadConversations = _filter(this.cachedUnreadConversations, 'hasUnreads')
      }
    },

    fetchMoreAll() {
      this.$apollo.queries.conversationList.fetchMore({
        variables: {
          tz: tz(),
          onlyUnread: false,
          teamId: this.teamId,
          messaged: true,
          messagedBefore: _get(_nth(this.conversationList.list, -1), 'messagedAt'),
          idBefore: _get(_nth(this.conversationList.list, -1), 'id')
        },
        updateQuery: (previousResult, {fetchMoreResult}) => {
          const newList = fetchMoreResult.conversationList.list
          const newCount = fetchMoreResult.conversationList.count
          const newRemaining = fetchMoreResult.conversationList.remaining

          return {
            conversationList: {
              __typename: previousResult.conversationList.__typename,
              list: _orderBy(_uniqBy([...newList, ...previousResult.conversationList.list], 'id'), ['messagedAt', 'id'], ['desc', 'desc']),
              count: newCount,
              remaining: newRemaining
            }
          }
        }
      })
    },

    fetchMoreUnread() {
      this.$apollo.queries.unreadConversationList.fetchMore({
        variables: {
          tz: tz(),
          onlyUnread: true,
          teamId: this.teamId,
          messaged: true,
          messagedBefore: _get(_nth(this.unreadConversationList.list, -1), 'messagedAt'),
          idBefore: _get(_nth(this.unreadConversationList.list, -1), 'id')
        },
        updateQuery: (previousResult, {fetchMoreResult}) => {
          const newList = fetchMoreResult.conversationList.list
          const newCount = fetchMoreResult.conversationList.count
          const newRemaining = fetchMoreResult.conversationList.remaining

          return {
            conversationList: {
              __typename: previousResult.conversationList.__typename,
              list: _orderBy(_uniqBy([...newList, ...previousResult.conversationList.list], 'id'), ['messagedAt', 'id'], ['desc', 'desc']),
              count: newCount,
              remaining: newRemaining
            }
          }
        }
      })
    },
  },

  computed: {

    anyRemaining() {
      return _get(this.conversationList, 'remaining') > 0
    },

    conversations() {
      var list = _get(this.conversationList, 'list', [])
      var unreadList = this.cachedUnreadConversations

      if (this.onlyUnread) {
        return _orderBy(unreadList, ['messagedAt', 'id'], ['desc', 'desc'])
      } else {
        return _orderBy(list, ['messagedAt', 'id'], ['desc', 'desc'])
      }
    },

    noConversations() {
      return this.conversations.length == 0
    },
  },

  watch: {
    unreadConversationList(newList, _oldList) {
      // Toss unreads in cachedUnreadConversations unless it's updating the state of an existing cached conversation.

      var newReads = _filter(newList.list, (convo) => { return !convo.hasUnreads })
      var newUnreads = _filter(newList.list, 'hasUnreads')
      var newTossableReads = _differenceBy(newReads, this.cachedUnreadConversations, 'id')
      var newUsableReads = _differenceBy(newReads, newTossableReads, 'id')
      this.cachedUnreadConversations = [...newUnreads, ...newUsableReads]
    }
  },

  apollo: {
    conversationList: {
      query: ConversationListQuery,
      variables() {
        return {
          tz: tz(),
          onlyUnread: false,
          teamId: this.teamId,
          messaged: true
        }
      },
      fetchPolicy: 'cache-first',
      subscribeToMore: {
        document: NewConversationSubscription,
        variables() {
          return {
            tz: tz(),
            teamId: this.teamId,
          }
        },
        updateQuery: ({conversationList}, {subscriptionData}) => {
          console.log('got subscription from All Conversations')
          let newItem = subscriptionData.data.newConversation
          let oldList = conversationList.list
          let newList = _uniqBy([newItem, ...oldList], 'id')

          return {
            conversationList: {
              __typename: conversationList.__typename,
              list: newList,
              count: conversationList.count,
              remaining: conversationList.remaining
            }
          }
        }
      }
    },

    unreadConversationList: {
      query: ConversationListQuery,
      variables() {
        return {
          tz: tz(),
          onlyUnread: true,
          teamId: this.teamId,
          messaged: true,
        }
      },
      update: data => data.conversationList,
      fetchPolicy: 'cache-first',
      subscribeToMore: {
        document: NewConversationSubscription,
        variables() {
          return {
            tz: tz(),
            teamId: this.teamId,
          }
        },
        updateQuery: ({conversationList}, {subscriptionData}) => {
          console.log('got subscription from Unread Conversations')
          let newItem = subscriptionData.data.newConversation
          let oldList = conversationList.list
          let newList = _uniqBy([newItem, ...oldList], 'id')

          return {
            conversationList: {
              __typename: conversationList.__typename,
              list: newList,
              count: conversationList.count,
              remaining: conversationList.remaining
            }
          }
        }
      }
    }
  }

}
</script>
